import { useContext, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { useQueryClient } from "@tanstack/react-query";
import RightArrowIcon from "../assets/common/right-arrow-icon.svg";
import SettingsIcon from "../assets/settings.svg";
import IntegrationsPage from "./IntegrationsPage";
import { GlobalContext } from "../context/GlobalContext";
import { getUserAttributes } from "../utils/auth";
import { convertToUTC, getRefreshDataDateRange, getSyncedAt } from "../utils/utils";
import { IntegrationTypes } from "../utils/types";
import CalendarService from "../services/calender/CalendarService";
import useDisplaySnackbar from "../utils/useDisplaySnackbar";
import useIntegrationStyles from "../components/Integrations/IntegrationStyles";

const SettingsPage = () => {
  const pathParts = location.pathname.split("/");
  const lastPart = pathParts[pathParts.length - 1];
  const { accountDetails } = useContext(GlobalContext);
  const loggedInEmail = getUserAttributes()?.email;
  const styles = useIntegrationStyles();
  const queryClient = useQueryClient();
  const { showSnackbar } = useDisplaySnackbar();
  const showCalendarSync = lastPart === "user-settings";
  const syncInProgressRef = useRef(false);
  const [refreshStartEndTime, setRefreshStartEndTime] = useState({
    start_time: "",
    end_time: "",
  });
  const [fetchCalendar, setFetchCalendar] = useState<
    { email: string; type: IntegrationTypes } | undefined
  >(undefined);

  const settingsLabel: Record<string, string> = {
    "user-settings": "User Level Settings",
    "notification-settings": "Notification Settings",
    "space-settings": "Space Settings",
    "app-config": "App Config.",
    "org-management": "Domain Management",
    "video-guides": "Video Guides",
  };

  useEffect(() => {
    if (!showCalendarSync) return;
    setRefreshStartEndTime(getRefreshDataDateRange());
  }, [showCalendarSync]);

  const {
    isLoading: isSyncLoading,
    isFetching: isSyncFetching,
    isSuccess: isSyncSuccess,
    isError: isSyncError,
    error: syncError,
  } = CalendarService.useSyncCalendar(
    {
      email: fetchCalendar?.email || "",
      calendarDomain: fetchCalendar?.type || "",
      startTime: convertToUTC(refreshStartEndTime.start_time),
      endTime: convertToUTC(refreshStartEndTime.end_time),
    },
    showCalendarSync && fetchCalendar !== undefined
  );

  useEffect(() => {
    if (!showCalendarSync || !syncInProgressRef.current) return;
    if (isSyncSuccess) {
      queryClient.invalidateQueries({ queryKey: ["get_meetings"] });
      queryClient.invalidateQueries({ queryKey: ["get_user_level_settings"] });
      queryClient.invalidateQueries({ queryKey: ["get_calendar_details"] });
      showSnackbar("success", "Calendar data is synced.");
      setFetchCalendar(undefined);
      syncInProgressRef.current = false;
      queryClient.removeQueries({ queryKey: ["sync_calendar"] });
    }
    if (isSyncError) {
      const errorMessage =
        (syncError as any)?.response?.data?.message ||
        (syncError as any)?.message ||
        "Failed to sync calendar";
      showSnackbar("error", errorMessage);
      setFetchCalendar(undefined);
      syncInProgressRef.current = false;
      queryClient.removeQueries({ queryKey: ["sync_calendar"] });
    }
  }, [isSyncSuccess, isSyncError, syncError, showCalendarSync]);

  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  const {
    data: calendarDetailsData,
    isLoading: isCalendarDetailsLoading,
    isFetching: isCalendarDetailsFetching,
  } = CalendarService.useGetCalendarDetails(
    { employeePorId: loggedInProfileOrgId },
    showCalendarSync && !!loggedInProfileOrgId
  );

  const rawDomain = (
    calendarDetailsData?.calendar_domain ||
    calendarDetailsData?.calendarDomain
  )?.toLowerCase();

  const calendarDomain: IntegrationTypes | undefined =
    rawDomain === IntegrationTypes.MICROSOFT
      ? IntegrationTypes.MICROSOFT
      : rawDomain === IntegrationTypes.GOOGLE
      ? IntegrationTypes.GOOGLE
      : undefined;

  const isCalendarDetailsPending =
    isCalendarDetailsLoading || (isCalendarDetailsFetching && !calendarDetailsData);
  const isCalendarEnabled =
    !isCalendarDetailsPending &&
    calendarDetailsData?.isSyncEnabled === true &&
    !!calendarDomain;
  const lastSyncTime = calendarDetailsData?.lastSyncTime;

  const tooltipTitle = isCalendarDetailsPending
    ? "Loading calendar details..."
    : !isCalendarEnabled
    ? "Calendar is disabled"
    : lastSyncTime
    ? getSyncedAt(lastSyncTime)
    : "Sync the calendar";

  const handleSyncNow = () => {
    if (
      isSyncLoading ||
      isSyncFetching ||
      !loggedInEmail ||
      !isCalendarEnabled ||
      !calendarDomain
    )
      return;

    syncInProgressRef.current = true;
    setRefreshStartEndTime(getRefreshDataDateRange());
    setFetchCalendar({
      email: loggedInEmail,
      type: calendarDomain,
    });
  };

  return (
    <Box
      id="meetingsParent"
      className="flex flex-col justify-start items-start gap-6 mt-2"
      sx={{ width: "100%", height: "calc(100vh - 50px)" }}
      >
      <Box
        sx={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "20px",
        }}
      >
        <Box
          className="interaction-breadcrumbs"
          sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
          }}
          >
          <img src={SettingsIcon} alt="" height={16} width={16} />
          <h2>Settings</h2>

          {settingsLabel[lastPart] && (
              <>
              <img src={RightArrowIcon} alt="" style={{ marginTop: "3px" }} />
              <h1 className="interactions-title">
                  {settingsLabel[lastPart]}
              </h1>
              </>
          )}
        </Box>
        {showCalendarSync && (
          <Tooltip
            title={tooltipTitle}
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  color: "#fff",
                  fontSize: "10px",
                },
              },
            }}
          >
            <Box component="span" sx={{ display: "inline-flex" }}>
              <Button
                className={styles.settingsCalendarStatus}
                size="small"
                variant="outlined"
                onClick={handleSyncNow}
                disabled={
                  isSyncLoading ||
                  isSyncFetching ||
                  !isCalendarEnabled ||
                  isCalendarDetailsPending
                }
                startIcon={
                  isSyncLoading || isSyncFetching ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SyncRoundedIcon className={styles.settingsSync} />
                  )
                }
              >
                <b className={styles.settingsSyncText}>Calendar Sync</b>
              </Button>
            </Box>
          </Tooltip>
        )}
      </Box>
      < IntegrationsPage />
    </Box>
  );
};

export default SettingsPage;
